import { DataTable } from "@constellation-academy/epic-ui";
import { useTranslation } from "@i18n/hooks";
import { VISIBILITY_CONFIG_TYPE_TO_KIND } from "@screens/offer-subscriptions/parts/edit-subscriber-config-form";
import { actionsClass, updateBadgeClass } from "./offer-subscriptions-table.styles";
import {
	OfferSubscriptionNode,
	OfferSubscriptionsTableProps,
} from "./offer-subscriptions-table.types";
import { BumpVersionButton } from "../bump-version-button";
import { EditSubscriberConfigButton } from "../edit-subscriber-config-button";

export const OfferSubscriptionsTable = ({
	subscriptions,
	onRefetch,
}: OfferSubscriptionsTableProps) => {
	const { t } = useTranslation("common");

	return (
		<DataTable<OfferSubscriptionNode[]>
			value={subscriptions}
			dataKey="offerId"
			emptyMessage={t("offerSubscriptions.empty")}
		>
			{(Column) => (
				<>
					<Column
						header={t("offerSubscriptions.columns.offer")}
						body={(row) => row.config.title ?? row.offerTitle}
					/>
					<Column
						header={t("offerSubscriptions.columns.owner")}
						body={(row) => row.owner}
						style={{ width: "10%" }}
					/>
					<Column
						header={t("offerSubscriptions.columns.versionInUse")}
						body={(row) => `v${row.versionInUse}`}
						style={{ width: "10%" }}
					/>
					<Column
						header={t("offerSubscriptions.columns.latestVersion")}
						style={{ width: "12%" }}
						body={(row) => (
							<span className={row.updateAvailable ? updateBadgeClass : undefined}>
								{`v${row.latestVersion}`}
								{row.updateAvailable && " ↑"}
							</span>
						)}
					/>
					<Column
						header={t("offerSubscriptions.columns.visibility")}
						style={{ width: "14%" }}
						body={(row) => {
							const configType = row.config.visibilityConfigs[0]?.configType;
							if (!configType)
								return t("offerSubscriptions.visibilityOptions.VISIBLE");
							const kind = VISIBILITY_CONFIG_TYPE_TO_KIND[configType];
							return kind ? t(`offerSubscriptions.visibilityOptions.${kind}`) : "—";
						}}
					/>
					<Column
						header={t("table.actions")}
						style={{ width: "22%" }}
						body={(row) => (
							<div className={actionsClass}>
								<EditSubscriberConfigButton
									offerId={row.offerId}
									currentTitle={row.config.title ?? null}
									currentImageUrl={row.config.image?.url ?? null}
									currentImageFileId={row.config.image?.id ?? null}
									currentVisibility={
										row.config.visibilityConfigs.length === 0
											? "VISIBLE"
											: VISIBILITY_CONFIG_TYPE_TO_KIND[
													row.config.visibilityConfigs[0]?.configType ??
														""
											  ] ?? null
									}
									currentDateTime={
										row.config.visibilityConfigs[0]?.dateTime?.toString() ??
										null
									}
									currentLicenseProduct={row.config.licenseProduct ?? undefined}
									currentCourseContentProduct={
										row.config.courseContentProduct ?? undefined
									}
									currentCertificationProduct={
										row.config.certificationProduct ?? undefined
									}
									onUpdated={onRefetch}
								/>
								{row.updateAvailable && (
									<BumpVersionButton
										offerId={row.offerId}
										latestVersion={row.latestVersion}
										onBumped={onRefetch}
									/>
								)}
							</div>
						)}
					/>
				</>
			)}
		</DataTable>
	);
};
